/**
 * chart-svg.js
 *
 * Draws SVG charts for the BMO Fund Central / Centre fonds BMO website.
 *
 * It auto-magically produces SVG charts under DOM elements with the "mfhq-chart" class.
 *
 * How to add a chart:
 *
 * 1. Place a <div class="mfhq-chart"> tag where you want a chart to
 * appear. Give it a unique id="myChartName" attribute.
 *
 * 2. Add attributes to the <div> tag to set options for the chart. Here's a
 * list of available options:
 * - mfhq-fundCode="BMO00000" | required Specify the fundCode of the mutual fund to
 *   be charted.
 * - mfhq-chart="chartname" | required Specify the name of the chart to be displayed.
 *   Options: [asset | calendar | geography | sector].
 * - mfhq-lang="en" | optional Language can be en or fr. Defaults to en.
 * - mfhq-width="800" | optional Width of the chart. Defaults to 500.
 * - mfhq-height="600" | optional Height of the chart. Defaults to 300.
 * - mfhq-fundname="Fund name for calendar return chart" | optional Display name
 *   of the fund. Only applicable to the calendar chart.
 *
 */


/**
 * Draw a bar chart for the given DOM element ID and params.
 *
 * Chart depicts past annual fund returns for up to ten years in either
 * English or French. Scales based on the width and height given in the
 * params dictionary.
 *
 * Dictionary can have the following:
 * - fundCode : Refinitiv ID or fundserv code of the mutual fund
 * - chart : Type of chart ['calendar']
 * - lang : ['en' | 'fr']
 * - fundName : Override display name in legend. Defaults to fund's legal name.
 * - width : Width of the chart SVG canvas.
 * - height : Height of the chart SVG canvas.
 * - fontSize : Size of text for the legend and returns labels.
 *
 * @param {string} elId - DOM element ID.
 * @param {object} params - Dictionary of parameters for the chart.
 */

const makeBar = async function(elId, params) {

	// Unpack the params object
	let fundCode = ('fundCode' in params ? params['fundCode'] : '');
	let chart = ('chart' in params ? params['chart'] : '');
	let lang = ('lang' in params ? params['lang'] : 'en');
	let _fundName = ('fundName' in params ? params['fundName'] : '');
	let width = ('width' in params ? Number(params['width']) : 500);
	let height = ('height' in params ? Number(params['height']) : 300);
	let fontSize = ('fontsize' in params ? Number(params['fontSize']) : 0);

	const FONT_SIZE = (Number(fontSize) > 0 ? Number(fontSize) : Math.round(width / 42));
	const MARGIN = {top: 20, right: 0, bottom: 30, left: 40};

	let barChartData = window.chartData[chartPropertyMap[chart]];
	if (barChartData == null) {
		console.warn(`No ${chart} data found to build bar chart.`)
		return
	}

	let data = [];
	barChartData.forEach((d, i) => data.push({
		_label: (new Date(d.end)).getFullYear(),
		_value: Number(parseFloat((d.value)).toFixed(4))
	}));

	data = data.sort((a,b) => d3.descending(a.value, b.value));
	const dataFormat = "";
	const dataY = "%";

	// There should not be more than 10 items, log it if there are
	if (data.length > 10)
		console.warn(`There are ${data.length} calendar items for ${fundCode}.`);

	// Situate the chart on the canvas
	let canvas = d3.select(`#${elId}`)
		.append("svg")
		.attr("width", width)
		.attr("height", height)
		.attr("font-family", "Heebo, sans-serif")
		.attr("font-size", FONT_SIZE);

	// Assign colours
	const color = "steelblue";

	// Create the legend
	const legendX = (width - (MARGIN.left + MARGIN.right)) * (1/2) + MARGIN.left;
	const legendY = MARGIN.top; //* (1/4);
	let legend = canvas.append("g")
		.attr("transform", `translate(${legendX}, ${legendY})`)
		.attr("font-family", "Heebo, sans-serif")
		.attr("font-size", FONT_SIZE);

	// Item name
	let legendText = legend.append("text")
		.attr("text-anchor", "middle")
		.text(_fundName);

	let textHeight = legendText.node().getBBox().height;
	let textWidth = legendText.node().getBBox().width;

	// Coloured box
	legend.append("rect")
		.attr("x", -((textWidth / 2) + 15))
		.attr("y", -10)
		.attr("width", 10)
		.attr("height", 10)
		.attr("fill", color);

	// Set up domain scale
	let x = d3.scaleBand()
		.domain(d3.range(data.length))
		.range([MARGIN.left, width - MARGIN.right])
		.padding(0.2);

	// Set up range scale
	let y = d3.scaleLinear()
		.domain([d3.min(data, d => Math.min(0, d._value)), d3.max(data, d => Math.max(0, d._value))]).nice()
		.range([height - MARGIN.bottom, MARGIN.top + textHeight + 15]);

	// Define axes
	let xAxis = g => g
		.attr("transform", `translate(0, ${height - MARGIN.bottom})`) //${y(0)})`)
		.call(d3.axisBottom(x).tickFormat(i => data[i]._label).tickSizeOuter(0))
		.call(g => g.select(".domain").remove());

	let xZeroAxis = g => g
		.attr("transform", `translate(0, ${y(0)})`)
		.style("stroke-dasharray", ("2, 1"))
		.call(d3.axisBottom(x).tickFormat(i => "").tickSizeInner(0).tickSizeOuter(0));

	let yAxis = g => g
		.attr("transform", `translate(${MARGIN.left}, 0)`)
		.call(d3.axisLeft(y).ticks(null, dataFormat))
		.call(g => g.append("text")
			.attr("transform", `translate(0, ${MARGIN.top + textHeight - 10})`)
			.attr("fill", "currentColor")
			.attr("text-anchor", "end")
			.text(dataY));

	// Draw the bars
	canvas.append("g")
		.attr("fill", color)
		.selectAll("rect")
		.data(data)
		.join("rect")
		.attr("x", (d, i) => x(i))
		.attr("y", d => (d._value >= 0 ? y(d._value) : y(0)))
		.attr("height", d => (d._value >= 0 ? y(0) - y(d._value) : y(d._value) - y(0)))
		.attr("width", x.bandwidth());

	// Value labels above bars
	canvas.append("g")
		.selectAll("text")
		.data(data)
		.join("text")
		.attr("x", (d, i) => x(i) + (x.bandwidth() / 2))
		.attr("y", d => (d._value >= 0 ? y(d._value) : y(0)) - 10)
		.attr("fill", "currentcolor")
		.attr("text-anchor", "middle")
		.attr("font-family", "Heebo, sans-serif")
		.attr("font-size", FONT_SIZE)
		.text(d => parseFloat(parseFloat(d._value).toFixed(1)).toLocaleString(lang) + '%');

	// Append the axes
	canvas.append("g")
		.call(xAxis);

	canvas.append("g")
		.call(xZeroAxis);

	canvas.append("g")
		.call(yAxis);

}

/*
	Convenient way to get property names based on the "chart"
 */
const chartPropertyMap = {
	calendar: 'annualPerformanceData',
	asset: 'assetAllocation',
	sectors: 'sectorAllocation',
	geo: 'geographicAllocation',
}

/**
 * Draw a pie chart for the given element ID and params.
 *
 * Can draw asset, geography or sector-based mutual fund holdings in English
 * or French. Scales to fit the width and height specified in the params.
 *
 * Dictionary can have the following:
 * - fundCode : Refinitiv ID or fundserv code of the mutual fund
 * - chart : Type of chart ['asset', 'geography', 'sector']
 * - lang : ['en' | 'fr']
 * - width : Width of the chart SVG canvas.
 * - height : Height of the chart SVG canvas.
 * - fontSize : Size of text for the legend and returns labels.
 *
 * @param {string} elId - DOM element ID.
 * @param {object} params - Dictionary of parameters for the chart.
 */
const makePie = async function(elId, params) { //, fundCode, chart, lang='en', width=500, height=300, fontSize=0) {

	// Unpack the params object
	let fundCode = ('fundCode' in params ? params['fundCode'] : '');
	let chart = ('chart' in params ? params['chart'] : '');
	let lang = ('lang' in params ? params['lang'] : 'en');
	let width = ('width' in params ? Number(params['width']) : 500);
	let height = ('height' in params ? Number(params['height']) : 300);
	let fontSize = ('fontsize' in params ? Number(params['fontSize']) : 0);

	const FONT_SIZE = (Number(fontSize) > 0 ? Number(fontSize) : Math.round(width / 42));

	let pieChartData = window.chartData[chartPropertyMap[chart]];
	if (pieChartData == null) {
		console.warn(`No data for pie chart: ${chart}`);
		return;
	}

	// Verify that data is a valid array, otherwise return
	if (! (Array.isArray(pieChartData) && pieChartData.length))
		return;

	// If there are more than 10 items, aggregate the rest as "Other"
	if (pieChartData.length > 10) {
		let arrOther = pieChartData.splice(10, pieChartData.length - 10);

		let otherVal = 0.0;
		if (Array.isArray(arrOther) && arrOther.length)
			arrOther.forEach((item) => otherVal += parseFloat(item.percentage));

		let otherItem = {
			percentage: Number(otherVal.toFixed(4)),
			name: (lang === 'fr' ? 'Autres' : 'Other')
		};

		pieChartData.push(otherItem);
	}

	// Sort by descending value.
	// Otherwise, D3 will mismatch the legend and pie slices.
	pieChartData.sort((a, b) => {
		if (a.percentage > b.percentage) {
			return -1;
		}
		if (a.percentage < b.percentage) {
			return 1;
		}
		return 0;
	});

	// Set up the pie layout
	let pie = d3.pie().value(d => d.percentage).padAngle(0.02)(pieChartData);

	// Calculate the arcs
	let radius = width * (19/80); //(20/80);
	let arcMkr = d3.arc()
		.innerRadius(radius * (2/3))
		.outerRadius(radius)
		.cornerRadius(1);

	// Assign colours
	let scColor = d3.scaleOrdinal(d3.schemePaired) //d3.schemePastel2)
		.domain(pie.map(d => d.index));

	// Situate the chart on the canvas
	let canvas = d3.select(`#${elId}`)
		.append("svg")
		.attr("width", width)
		.attr("height", height);

	// Draw the pie
	let pieChartX = 0;
	let pieChartY = 0;
	let pieChart_width = (40/80) * width;
	let pieChart_height = height;
	let pieChart = canvas.append("g")
		.attr("transform", `translate(${pieChart_width/2}, ${pieChart_height/2})`)
		.selectAll("path")
		.data(pie)
		.enter()
		.append("path")
		.attr("d", arcMkr)
		.attr("fill", d => scColor(d.index));
	// .attr("stroke", "grey");

	// Create the legend
	const legendX = width * (47/80);
	const legendY = (height / 2) - (pieChartData.length * FONT_SIZE * (4/3) / 2);
	let legend = canvas.append("g")
		.attr("transform", `translate(${legendX}, ${legendY})`)
		.attr("font-family", "Heebo, sans-serif")
		.attr("font-size", FONT_SIZE);

	// Add the legend labels
	pieChartData.forEach((d, i) => {
		const row = legend.append("g")
			.attr("transform", `translate(0, ${i * FONT_SIZE * (4/3)})`);

		// Coloured box
		row.append("rect")
			.attr("width", 10)
			.attr("height", 10)
			.attr("fill", scColor(i));

		// Item name
		row.append("text")
			.attr("x", 20)
			.attr("y", 10)
			.attr("text-anchor", "start")
			.text(d.name);

		// Item percentage
		row.append("text")
			.attr("x", -10)
			.attr("y", 10)
			.attr("text-anchor", "end")
			.text(Number((d.percentage).toFixed(1)).toLocaleString(lang) + '%');

	});

}

/**
 * Draw (or re-draw) the charts on this page.
 *
 * Pass an array of DOM element IDs to redraw multiple charts by ID, or pass nothing to
 * redraw all of the charts on the page. You can also pass a single ID to only
 * draw that chart.
 *
 * @param {string} id - The DOM id of the element to redraw. Alternatively, pass an array
 * of string IDs to redraw multiple charts or {null} to redraw all charts on the page.
 * @param {object} fund - The data already retrieved from Algolia
 */
export function drawChart(id = null) {

	const findChartMaker = (c) => {
		const mapChartMaker = {
			"calendar": makeBar,
			"asset": makePie,
			"sector": makePie,
			"geography": makePie,

			"_DEFAULT": makePie,
		};

		return (c in mapChartMaker ? mapChartMaker[c] : mapChartMaker["_DEFAULT"]);
	};

	const drawId = function(d, i) {
		let chart_id = d3.select(this).attr("id");
		let chart_code = d3.select(this).attr("mfhq-fundCode");
		let chart_name = d3.select(this).attr("mfhq-fundName");
		let chart_chart = d3.select(this).attr("mfhq-chart");
		let chart_lang = d3.select(this).attr("mfhq-lang");
		let chart_width = d3.select(this).attr("mfhq-width");
		let chart_height = d3.select(this).attr("mfhq-height");

		findChartMaker(chart_chart)(chart_id, {
			"fundCode": chart_code,
			"fundName": chart_name,
			"chart": chart_chart,
			"lang": chart_lang,
			"width": chart_width,
			"height": chart_height
		});
	};

	// When no id is provided draw all charts
	if (!id) {
		d3.selectAll(".mfhq-chart>svg").remove();
		d3.selectAll(".mfhq-chart").each(drawId);
		return;
	}

	// Draw charts of IDs that were passed in.
	let arrChartIds = (typeof(id) === 'string' ? [ id ] : (Array.isArray(id) ? id : []));
	arrChartIds.forEach(i => {
		let nodeSel = '#' + i;

		// Remove any existing SVG elements
		d3.select(nodeSel + '>svg').remove();

		// Draw the chart
		d3.select(nodeSel).each(drawId);
	});
}
